/**
 * Utilitário para agrupar itens por data
 * Agrupa atividades, mensagens, etc por dia para exibição em timeline
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10 v1.0.0 - Criado para timeline premium estilo Bitrix
 */
import { startOfDay, isToday, isYesterday, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Interface para item agrupável (deve ter created_at)
 */
export interface GroupableItem {
  created_at: string;
}

/**
 * Interface do grupo de data retornado
 */
export interface DateGroup<T> {
  dateLabel: string;       // "Hoje", "Ontem", "10 de janeiro de 2026"
  dateKey: string;         // "2026-01-10" para uso como key de React
  items: T[];              // Items deste grupo
}

/**
 * Formata label da data de forma amigável
 * - "Hoje" para data atual
 * - "Ontem" para data anterior
 * - "DD de MMMM de YYYY" para outras datas
 * 
 * @param date - Data a formatar
 * @returns Label formatado
 */
export function formatDateLabel(date: Date): string {
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
}

/**
 * Agrupa items por data de criação
 * Retorna array de grupos ordenados do mais recente ao mais antigo
 * 
 * @param items - Array de items com created_at
 * @returns Array de grupos { dateLabel, dateKey, items }
 * 
 * @example
 * const activities = [{ created_at: '2026-01-10T14:00:00' }, ...];
 * const groups = groupByDate(activities);
 * // [{ dateLabel: 'Hoje', dateKey: '2026-01-10', items: [...] }, ...]
 */
export function groupByDate<T extends GroupableItem>(items: T[]): DateGroup<T>[] {
  // Mapa para agrupar items por data (chave = YYYY-MM-DD)
  const groupsMap = new Map<string, { date: Date; items: T[] }>();
  
  for (const item of items) {
    const itemDate = new Date(item.created_at);
    const dayStart = startOfDay(itemDate);
    const dateKey = format(dayStart, 'yyyy-MM-dd');
    
    if (!groupsMap.has(dateKey)) {
      groupsMap.set(dateKey, { date: dayStart, items: [] });
    }
    groupsMap.get(dateKey)!.items.push(item);
  }
  
  // Converte map para array e ordena do mais recente ao mais antigo
  const groups = Array.from(groupsMap.entries())
    .map(([dateKey, { date, items: groupItems }]) => ({
      dateKey,
      dateLabel: formatDateLabel(date),
      items: groupItems,
    }))
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  
  return groups;
}

/**
 * Agrupa items por data mantendo ordem original dentro de cada grupo
 * Útil quando items já vêm ordenados do backend
 * 
 * @param items - Array de items com created_at já ordenado
 * @returns Array de grupos preservando ordem interna
 */
export function groupByDatePreserveOrder<T extends GroupableItem>(items: T[]): DateGroup<T>[] {
  if (items.length === 0) return [];
  
  const groups: DateGroup<T>[] = [];
  let currentGroup: DateGroup<T> | null = null;
  
  for (const item of items) {
    const itemDate = new Date(item.created_at);
    const dayStart = startOfDay(itemDate);
    const dateKey = format(dayStart, 'yyyy-MM-dd');
    
    // Se mudou de data, cria novo grupo
    if (!currentGroup || currentGroup.dateKey !== dateKey) {
      currentGroup = {
        dateKey,
        dateLabel: formatDateLabel(dayStart),
        items: [],
      };
      groups.push(currentGroup);
    }
    
    currentGroup.items.push(item);
  }
  
  return groups;
}
